import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'

export interface AutoAssignRuleRequestPermissions {
    canView: boolean
    canEdit: boolean
    canDelete: boolean
    canSubmit: boolean
    canTest: boolean
    canApprove: boolean
}

/**
 * Permission gate for AutoAssignRuleRequestDetail page (and the wizard in 603.08).
 *
 * The five write permissions are isolated as their own fields so callers can
 * gate each affordance independently. Even if today's backend wires multiple
 * flags to the same role, future decoupling does not break call sites.
 *
 * - {@code canView}    — read access to detail page.
 * - {@code canEdit}    — edit metadata (DRAFT only).
 * - {@code canDelete}  — delete the request (DRAFT only).
 * - {@code canSubmit}  — submit DRAFT for approval.
 * - {@code canTest}    — run the rule's test endpoint.
 * - {@code canApprove} — approve/reject a SUBMITTED request.
 */
export function useAutoAssignRuleRequestPermissions(): AutoAssignRuleRequestPermissions {
    const {hasPermission} = useAuth()
    return {
        canView: hasPermission(permissions.autoAssignRules.view),
        canEdit: hasPermission(permissions.autoAssignRules.update),
        canDelete: hasPermission(permissions.autoAssignRules.delete),
        canSubmit: hasPermission(permissions.autoAssignRules.submit),
        canTest: hasPermission(permissions.autoAssignRules.test),
        canApprove: hasPermission(permissions.autoAssignRules.approve),
    }
}
